/**
 * AlbumManagerPanel — the "obvious" album management UI.
 *
 * Shows each of the artist's albums as a big card with:
 *   • Cover + title
 *   • "X / 15 tracks" progress
 *   • "Manage songs" button → opens a modal listing every one of the artist's
 *     tracks with a checkbox. Ticking includes the track in this album;
 *     unticking removes it (moves back to Standalone Single).
 *   • Tracks already in *another* album show up disabled with a note so the
 *     artist can't accidentally steal them from another record.
 *
 * Replaces the tiny inline album dropdown that lived in the My Music row.
 * Founder bypasses the 15-track cap.
 */
import { useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Disc, Plus, Music, ImagePlus, GripVertical, ListOrdered, Sparkles, Calendar } from "lucide-react";
import SocialKitModal from "@/components/SocialKitModal";
import RolloutPlanModal from "@/components/RolloutPlanModal";

const CAP = 15;

export default function AlbumManagerPanel({ tracks, albums, bypass, onTracksChanged, onAlbumsChanged }) {
  const [openAlbumId, setOpenAlbumId] = useState(null);
  const [reorderAlbumId, setReorderAlbumId] = useState(null);
  const [socialAlbumId, setSocialAlbumId] = useState(null);
  const [rolloutAlbumId, setRolloutAlbumId] = useState(null);
  // Precompute the ordered track list for the reorder dialog only when the
  // selected album or the tracks array actually changes — prevents a fresh
  // sorted array on every keystroke elsewhere in the parent.
  const reorderTracks = useMemo(() => {
    if (!reorderAlbumId) return [];
    return tracks
      .filter((t) => t.album_id === reorderAlbumId)
      .sort((a, b) => (a.order_in_album ?? 999) - (b.order_in_album ?? 999) ||
                      (a.created_at || "").localeCompare(b.created_at || ""));
  }, [tracks, reorderAlbumId]);
  if (!albums || albums.length === 0) {
    return (
      <div data-testid="album-manager-empty"
           className="border border-dashed border-[#2A2A2A] p-6 mb-6 flex items-center gap-4">
        <div className="h-12 w-12 grid place-items-center bg-[#FF4500]/10 border border-[#FF4500]/30">
          <ImagePlus className="h-6 w-6 text-[#FF4500]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base">No albums yet</div>
          <div className="font-mono text-[11px] text-gray-500 mt-1">
            Go to <span className="text-[#FF4500]">Upload → New album</span> to create your first record. Then come back here to add songs.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="mb-8">
      <div className="mb-3 flex items-center gap-2">
        <Disc className="h-4 w-4 text-[#FF4500]" />
        <h3 className="font-display text-lg">Your albums</h3>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 ml-2">
          Tap "Manage songs" to bundle uploaded singles into a record
        </span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {albums.map((a) => {
          const count = tracks.filter((t) => t.album_id === a.id).length;
          return (
            <div key={a.id} data-testid={`album-card-${a.id}`}
                 className={`border ${a.is_free ? "border-[#FF4500]" : "border-[#2A2A2A]"} bg-[#121212] p-3`}>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 bg-neutral-800 shrink-0 overflow-hidden">
                  {a.cover_path
                    ? <img src={fileUrl(a.cover_path)} alt="" className="h-full w-full object-cover" />
                    : <div className="h-full w-full grid place-items-center text-gray-600"><Disc className="h-8 w-8" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display truncate flex items-center gap-2">
                    <span className="truncate">{a.title}</span>
                    {a.is_free && <span className="font-mono text-[9px] uppercase tracking-widest text-[#FF4500] bg-[#FF4500]/10 border border-[#FF4500]/40 px-1.5 py-0.5">FREE</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${count >= CAP && !bypass ? "text-[#FF4500]" : "text-gray-500"}`}>
                      {count} / {bypass ? "∞" : CAP} tracks
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  data-testid={`album-manage-${a.id}`}
                  onClick={() => setOpenAlbumId(a.id)}
                  className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-[10px] uppercase tracking-widest h-8 px-3"
                >
                  <Plus className="h-3 w-3 mr-1" /> Manage songs
                </Button>
                <Button
                  data-testid={`album-reorder-${a.id}`}
                  onClick={() => setReorderAlbumId(a.id)}
                  disabled={count === 0}
                  variant="outline"
                  className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-[10px] uppercase tracking-widest h-8 px-3 disabled:opacity-40"
                >
                  <ListOrdered className="h-3 w-3 mr-1" /> Order
                </Button>
                <Button
                  data-testid={`album-social-${a.id}`}
                  onClick={() => setSocialAlbumId(a.id)}
                  variant="outline"
                  className="rounded-none border-[#FF4500]/50 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] bg-transparent font-mono text-[10px] uppercase tracking-widest h-8 px-3"
                  title="Generate ready-to-post social copy with AI"
                >
                  <Sparkles className="h-3 w-3 mr-1" /> AI Social
                </Button>
                <Button
                  data-testid={`album-rollout-${a.id}`}
                  onClick={() => setRolloutAlbumId(a.id)}
                  variant="outline"
                  className="rounded-none border-[#FF4500]/50 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] bg-transparent font-mono text-[10px] uppercase tracking-widest h-8 px-3"
                  title="Generate a 21-day rollout plan with dated posts"
                >
                  <Calendar className="h-3 w-3 mr-1" /> Rollout Plan
                </Button>
                <label
                  data-testid={`album-free-toggle-${a.id}`}
                  className="ml-auto flex items-center gap-2 cursor-pointer"
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Free</span>
                  <Switch
                    checked={!!a.is_free}
                    onCheckedChange={async (v) => {
                      try {
                        await api.patch(`/albums/${a.id}/free`, { is_free: v });
                        toast.success(v ? `"${a.title}" is now a free download` : `"${a.title}" back to $15`);
                        onAlbumsChanged && onAlbumsChanged();
                        onTracksChanged && onTracksChanged();
                      } catch (e) { toast.error(errMsg(e)); }
                    }}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {openAlbumId && (
        <ManageDialog
          key={openAlbumId}
          album={albums.find((a) => a.id === openAlbumId)}
          tracks={tracks}
          bypass={bypass}
          onClose={() => setOpenAlbumId(null)}
          onSaved={() => { setOpenAlbumId(null); onTracksChanged && onTracksChanged(); }}
        />
      )}

      {reorderAlbumId && (
        <ReorderDialog
          key={`reorder-${reorderAlbumId}`}
          album={albums.find((a) => a.id === reorderAlbumId)}
          tracks={reorderTracks}
          onClose={() => setReorderAlbumId(null)}
          onSaved={() => { setReorderAlbumId(null); onTracksChanged && onTracksChanged(); }}
        />
      )}

      {socialAlbumId && (() => {
        const a = albums.find((x) => x.id === socialAlbumId);
        return a ? (
          <SocialKitModal
            key={`social-${socialAlbumId}`}
            kind="album"
            id={socialAlbumId}
            title={a.title}
            onClose={() => setSocialAlbumId(null)}
          />
        ) : null;
      })()}

      {rolloutAlbumId && (() => {
        const a = albums.find((x) => x.id === rolloutAlbumId);
        return a ? (
          <RolloutPlanModal
            key={`rollout-${rolloutAlbumId}`}
            albumId={rolloutAlbumId}
            albumTitle={a.title}
            onClose={() => setRolloutAlbumId(null)}
          />
        ) : null;
      })()}
    </div>
  );
}


/** Drag-and-drop track ordering. Native HTML5 DnD — no library needed.
 *  Grip icon on each row is the drag handle; drop lines appear as you hover
 *  another row. Save writes the full ordered list to the backend in one call. */
function ReorderDialog({ album, tracks, onClose, onSaved }) {
  const [order, setOrder] = useState(tracks.map((t) => t.id));
  const [dragging, setDragging] = useState(null);
  const [busy, setBusy] = useState(false);

  const onDragStart = (id) => (e) => {
    setDragging(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (!dragging || dragging === id) return;
    setOrder((prev) => {
      const next = prev.filter((x) => x !== dragging);
      const targetIdx = next.indexOf(id);
      next.splice(targetIdx, 0, dragging);
      return next;
    });
  };
  const onDragEnd = () => setDragging(null);

  const move = (id, delta) => {
    setOrder((prev) => {
      const idx = prev.indexOf(id);
      const to = idx + delta;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/albums/${album.id}/reorder`, { track_ids: order });
      toast.success("Track order saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const byId = new Map(tracks.map((t) => [t.id, t]));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="album-reorder-dialog" className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Order tracks · "{album.title}"</DialogTitle>
          <div className="font-mono text-[11px] text-gray-500 mt-1">
            Drag the <GripVertical className="inline h-3 w-3" /> handle to rearrange, or use the ▲ ▼ buttons on mobile.
          </div>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto border border-[#2A2A2A] mt-2">
          {order.length === 0 && (
            <div className="p-8 text-center font-mono text-xs text-gray-500 uppercase">No tracks on this album yet.</div>
          )}
          {order.map((id, idx) => {
            const t = byId.get(id);
            if (!t) return null;
            const isDragging = dragging === id;
            return (
              <div
                key={id}
                data-testid={`reorder-row-${id}`}
                draggable
                onDragStart={onDragStart(id)}
                onDragOver={onDragOver(id)}
                onDragEnd={onDragEnd}
                className={`grid grid-cols-[auto_auto_1fr_auto] gap-2 items-center px-3 py-2.5 border-b border-[#2A2A2A] last:border-b-0 cursor-grab active:cursor-grabbing ${isDragging ? "opacity-40 bg-[#FF4500]/10" : "hover:bg-white/[0.03]"}`}
              >
                <GripVertical className="h-4 w-4 text-gray-500 shrink-0" />
                <span className="font-mono text-[10px] text-[#FF4500] tabular-nums w-6 text-center">{idx + 1}</span>
                <div className="min-w-0">
                  <div className="font-display truncate flex items-center gap-2">
                    <Music className="h-3 w-3 text-gray-500 shrink-0" />
                    <span className="truncate">{t.title}</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 truncate">{t.genre || "—"}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    data-testid={`reorder-up-${id}`}
                    onClick={() => move(id, -1)}
                    disabled={idx === 0}
                    className="h-6 w-6 grid place-items-center border border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px]"
                    title="Move up"
                  >▲</button>
                  <button
                    data-testid={`reorder-down-${id}`}
                    onClick={() => move(id, 1)}
                    disabled={idx === order.length - 1}
                    className="h-6 w-6 grid place-items-center border border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px]"
                    title="Move down"
                  >▼</button>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button
            data-testid="reorder-cancel"
            onClick={onClose}
            variant="outline"
            className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-10 px-4"
          >
            Cancel
          </Button>
          <Button
            data-testid="reorder-save"
            onClick={save}
            disabled={busy || order.length === 0}
            className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-10 px-6"
          >
            Save order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageDialog({ album, tracks, bypass, onClose, onSaved }) {
  // Snapshot current album membership once per (album, tracks) change so the
  // closure in save() has a stable reference instead of rebuilding every render.
  const initial = useMemo(
    () => new Set(tracks.filter((t) => t.album_id === album.id).map((t) => t.id)),
    [tracks, album.id],
  );
  const [selected, setSelected] = useState(initial);
  const [busy, setBusy] = useState(false);

  const toggle = (t) => {
    if (t.album_id && t.album_id !== album.id) return;   // locked in another album
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
      return next;
    });
  };

  const overCap = !bypass && selected.size > CAP;

  const save = async () => {
    if (overCap) { toast.error(`Max ${CAP} songs per album`); return; }
    setBusy(true);
    const toAdd = [...selected].filter((id) => !initial.has(id));
    const toRemove = [...initial].filter((id) => !selected.has(id));
    try {
      for (const id of toAdd) {
        await api.patch(`/tracks/${id}/album`, { album_id: album.id });
      }
      for (const id of toRemove) {
        await api.patch(`/tracks/${id}/album`, { album_id: null });
      }
      toast.success(`"${album.title}" updated · ${selected.size} songs`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="album-manage-dialog" className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add songs to "{album.title}"</DialogTitle>
          <div className="font-mono text-[11px] text-gray-500 mt-1">
            Tick every song that belongs on this record. {bypass ? "No limit (Founder access)" : `Up to ${CAP} songs per album`}.
          </div>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto border border-[#2A2A2A] divide-y divide-[#2A2A2A] mt-2">
          {tracks.length === 0 && (
            <div className="p-8 text-center font-mono text-xs text-gray-500 uppercase">Upload a song first — then come back here.</div>
          )}
          {tracks.map((t) => {
            const isInAnother = t.album_id && t.album_id !== album.id;
            const checked = selected.has(t.id);
            return (
              <label key={t.id} data-testid={`album-manage-row-${t.id}`}
                     className={`grid grid-cols-[auto_1fr_auto] gap-3 items-center px-3 py-2.5 ${isInAnother ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-white/[0.03]"}`}>
                <Checkbox
                  data-testid={`album-manage-check-${t.id}`}
                  checked={checked}
                  disabled={isInAnother}
                  onCheckedChange={() => toggle(t)}
                  className="rounded-none border-[#2A2A2A] data-[state=checked]:bg-[#FF4500] data-[state=checked]:border-[#FF4500]"
                />
                <div className="min-w-0">
                  <div className="font-display truncate flex items-center gap-2">
                    <Music className="h-3 w-3 text-gray-500 shrink-0" />
                    <span className="truncate">{t.title}</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
                    {isInAnother ? "Already in another album" : t.genre || "—"}
                  </div>
                </div>
                {t.is_free && <span className="font-mono text-[9px] text-[#FF4500] uppercase tracking-widest">Free</span>}
              </label>
            );
          })}
        </div>

        {overCap && (
          <div className="border border-red-500/40 bg-red-500/10 px-3 py-2 mt-3 font-mono text-[11px] text-red-300">
            {selected.size} selected · max {CAP} — untick a few
          </div>
        )}

        <DialogFooter className="mt-4 gap-2">
          <div className="font-mono text-xs text-gray-500 mr-auto">{selected.size} selected</div>
          <Button
            data-testid="album-manage-cancel"
            onClick={onClose}
            variant="outline"
            className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-10 px-4"
          >
            Cancel
          </Button>
          <Button
            data-testid="album-manage-save"
            onClick={save}
            disabled={busy || overCap}
            className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-10 px-6"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
